import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { designActions } from '../../actions';
import { ReactSVG } from 'react-svg';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { generateImageLink } from '../../utils/design.utils';
import './VSvg.css';

const VSvg = props => {
  const dispatch = useDispatch();
  const authToken = useSelector(state => state.authentication.user.authToken);
  const entity = useSelector(state => state.design.entitiesState[props.entityId]);
  const svgState = useSelector(state => state.design.svgsState[props.entityId]);

  const extension = entity.src?.split("/api")[1]?.split("?")[0]?.split(".")[1];
  let src = generateImageLink(entity.src, authToken);
  let isImage = false;
  if (extension !== 'svg') {
    src = `${entity.src}`;
    isImage = true;
  }

  const afterInjection = (error, svg) => {
    if (error) {
      return;
    }
    const svgNodes = [...svg.children];
    const paths = [
      ...svg.querySelectorAll('path, circle, rect, polygon, image, ellipse, polyline'),
    ];
    if (paths.length > 7) return;
    svgNodes.forEach(svgNode => {
      const svgSubNodes = [...svgNode.children];
      if (svgSubNodes && svgSubNodes.length > 0) {
        svgSubNodes.forEach(svgSubNode => {
          if (svgSubNode.id) {
            const subNodeId = svgSubNode.id;
            if (subNodeId.substring(0, 3) !== `v${props.entityId}_`)
              svgSubNode.id = `v${props.entityId}_${subNodeId}`;
          }
        });
      } else if (svgNode.id) {
        const nodeId = svgNode.id;
        if (nodeId.substring(0, 3) !== `v${props.entityId}_`)
          svgNode.id = `v${props.entityId}_${nodeId}`;
      }
    });

    if (!svgState || svgState.length === 0) {
      let colorList = [];
      let svgPath;

      paths.forEach(path => {
        const fillStyle = path.style ? path.style.fill : null;
        const fillAttr = path.getAttribute('fill');
        const strokeStyle = path.style ? path.style.stroke : null;
        const strokeAttr = path.getAttribute('stroke');
        
        const fillUrl = fillStyle || fillAttr || 'none';
        const strokeColor = strokeStyle || strokeAttr || 'none';

        if (fillUrl.substring(0, 3) === 'url') {
          const newFillUrl = `#v${props.entityId}_${fillUrl.split('"')[1].replace('#', '')}`;
          if (path.style) {
            path.style.fill = `url("${newFillUrl}")`;
          } else {
            path.setAttribute('fill', `url("${newFillUrl}")`);
          }
          const urlElement = svg.querySelector(newFillUrl);
          if (urlElement) {
            const urlNodes = [...urlElement.children];
            urlNodes.forEach(urlNode => {
              const fillColor = urlNode.style ? 
                (urlNode.style.stopColor || urlNode.getAttribute('stop-color')) : 
                urlNode.getAttribute('stop-color');
              svgPath = { id: urlNode.id, fill: fillColor || '#000000' };
              colorList.push(svgPath);
            });
          }
        } else {
          svgPath = { 
            id: path.id, 
            fill: fillUrl === 'none' ? '#000000' : fillUrl,
            stroke: strokeColor
          };
          colorList.push(svgPath);
        }
      });

      if (colorList.length > 0) {
        dispatch(designActions.changeSVGState({ [props.entityId]: colorList }));
      }
    } else {
      if (paths.length > 7) return;
      let mainIndex = 0;
      paths.forEach(path => {
        const fillStyle = path.style ? path.style.fill : null;
        const fillAttr = path.getAttribute('fill');
        const fillUrl = fillStyle || fillAttr || 'none';

        if (fillUrl.substring(0, 3) === 'url') {
          const newFillUrl = `#v${props.entityId}_${fillUrl.split('"')[1].replace('#', '')}`;
          if (path.style) {
            path.style.fill = `url("${newFillUrl}")`;
          } else {
            path.setAttribute('fill', `url("${newFillUrl}")`);
          }
          if (svgState[mainIndex] && svgState[mainIndex].stroke) {
            if (path.style) {
              path.style.stroke = svgState[mainIndex].stroke;
            } else {
              path.setAttribute('stroke', svgState[mainIndex].stroke);
            }
          }

          const urlElement = svg.querySelector(newFillUrl);
          if (urlElement) {
            const urlNodes = [...urlElement.children];
            urlNodes.forEach((urlNode, index) => {
              if (svgState[mainIndex]) {
                if (urlNode.style) {
                  urlNode.style.stopColor = svgState[mainIndex].fill;
                } else {
                  urlNode.setAttribute('stop-color', svgState[mainIndex].fill);
                }
              }
              mainIndex++;
            });
          }
        } else {
          if (svgState[mainIndex]) {
            if (path.style) {
              path.style.fill = svgState[mainIndex].fill;
              if (svgState[mainIndex].stroke) {
                path.style.stroke = svgState[mainIndex].stroke;
              }
            } else {
              path.setAttribute('fill', svgState[mainIndex].fill);
              if (svgState[mainIndex].stroke) {
                path.setAttribute('stroke', svgState[mainIndex].stroke);
              }
            }
          }
          mainIndex++;
        }
      });
    }
  };

  const beforeInjection = svg => {
    svg.classList.add('svg-class-name');
    svg.setAttribute(
      'style',
      `width: ${parseFloat(_.get(entity, 'style.inner.width'))}px; height: ${parseFloat(
        _.get(entity, 'style.inner.height'),
      )}px;`,
    );
  };

  const fallback = () => {
    return <img src={src} className="vectorImage" alt="" />;
  };

  const loading = () => {
    return <span>Loading</span>;
  };

  const {
    'transform-origin': legacyTransformOrigin,
    ...masterStyle
  } = entity.style.master || {};

  return (
    <div
      data-group={props.groupId}
      id={`_${props.entityId}_`}
      data-lock={entity.lock}
      data-type={props.dataType}
      data-page={props.pageId}
      className={props.className}
      data-target={props.dataTarget}
      style={{
        ...masterStyle,
        transformOrigin: masterStyle.transformOrigin || legacyTransformOrigin,
      }}
      onMouseDown={props.onMouseDown}
      onMouseOver={props.onMouseOver}
    >
      <div className="fullWH overflowHidden">
        <div className="fullWH flipper" style={entity.style.flip}>
          <div className="holder" style={entity.style.inner}>
            {isImage ? (
              <img src={src} className="vectorImage" alt="" />
            ) : (
              <ReactSVG
                src={src}
                afterInjection={afterInjection}
                beforeInjection={beforeInjection}
                evalScripts="always"
                fallback={fallback}
                loading={loading}
                renumerateIRIElements={false}
                wrapper="span"
                className="swrapper"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

VSvg.propTypes = {
  entityId: PropTypes.number,
  pageId: PropTypes.number,
  dataType: PropTypes.any,
  className: PropTypes.string,
  dataTarget: PropTypes.string,
  onMouseDown: PropTypes.func,
  onMouseOver: PropTypes.func,
};

export default VSvg;
